/// <reference types="google.picker" />
import { useContext, useEffect, useState } from 'react';
import { type ExternalFile } from '@/lib/types';
import getGoogleAccessToken from '../../utils/google/getGoogleAccessToken';
import IsGoogleDrivePickerOpenContext from '../../../../utils/IsGoogleDrivePickerOpenContext';
import * as Sentry from '@sentry/react';
import { MAX_EXTERNAL_FILES_SELECTED } from '../../utils/constants';
import loadGooglePickerApi from './loadGooglePickerApi';
import convertPickerDocumentToExternalFile from './convertPickerDocumentToExternalFile';

const GOOGLE_APP_ID = import.meta.env.VITE_GOOGLE_APP_ID;
const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;

const useGooglePicker = (): [
  boolean,
  boolean,
  () => Promise<ExternalFile[]>,
] => {
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isGoogleDrivePickerOpen, setIsGoogleDrivePickerOpen] = useContext(
    IsGoogleDrivePickerOpenContext,
  );

  useEffect(() => {
    if (!isGoogleDrivePickerOpen) {
      // Fix issues caused by Google Drive picker clashing with our focus
      // management causing incorrect pointer-events settings
      setTimeout(() => {
        for (const element of document.querySelectorAll('.MuiDialog-root')) {
          element.removeAttribute('data-focus-on-hidden');
        }
      }, 200);
    }
  }, [isGoogleDrivePickerOpen]);

  const showGooglePicker = async (): Promise<ExternalFile[]> => {
    setLoading(true);
    setHasError(false);

    try {
      await loadGooglePickerApi();

      const accessToken = await getGoogleAccessToken();

      return new Promise((resolve): void => {
        const handleGooglePickerEvent = (
          event: google.picker.ResponseObject,
        ): void => {
          if (
            event[google.picker.Response.ACTION] === google.picker.Action.CANCEL
          ) {
            setIsGoogleDrivePickerOpen(false);
            setLoading(false);
            resolve([]);

            return;
          }

          if (
            event[google.picker.Response.ACTION] === google.picker.Action.ERROR
          ) {
            setIsGoogleDrivePickerOpen(false);
            setHasError(true);
            setLoading(false);
            resolve([]);

            return;
          }

          if (
            event[google.picker.Response.ACTION] !== google.picker.Action.PICKED
          ) {
            return;
          }

          setIsGoogleDrivePickerOpen(false);
          setLoading(false);

          const pickedFiles = event[google.picker.Response.DOCUMENTS];

          if (typeof pickedFiles === 'undefined' || pickedFiles.length === 0) {
            resolve([]);

            return;
          }

          const externalFiles = pickedFiles
            .map(convertPickerDocumentToExternalFile)
            .filter(externalFile => externalFile !== null);

          resolve(externalFiles);
        };

        const googlePicker = new google.picker.PickerBuilder()
          .addView(google.picker.ViewId.DOCS)
          .setOAuthToken(accessToken.getValue())
          .setDeveloperKey(GOOGLE_API_KEY)
          .setCallback(handleGooglePickerEvent)
          .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
          .setMaxItems(MAX_EXTERNAL_FILES_SELECTED)
          .setAppId(GOOGLE_APP_ID)
          .build();

        googlePicker.setVisible(true);

        setIsGoogleDrivePickerOpen(true);
      });
    } catch (error) {
      Sentry.captureException(error);

      setHasError(true);
      setLoading(false);

      return [];
    }
  };

  return [loading, hasError, showGooglePicker];
};

export default useGooglePicker;
